import React, { useState, useEffect, useRef } from "react";
import {
  FaRegSmile,
  FaMicrophone,
  FaFileAudio,
  FaFileVideo,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileAlt,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileCsv,
  FaFile,
  FaFont,
} from "react-icons/fa";
import { IoIosCloseCircle, IoMdAdd, IoMdClose } from "react-icons/io";
import { LuCalendarPlus2 } from "react-icons/lu";
import EmojiPicker from "emoji-picker-react";
import "react-datepicker/dist/react-datepicker.css";
import { HiOutlineMicrophone } from "react-icons/hi2";
import { HiOutlineUser } from "react-icons/hi2";
import { TfiGallery } from "react-icons/tfi";
import { SlLocationPin } from "react-icons/sl";
import { IoDocumentTextOutline, IoSendSharp } from "react-icons/io5";
import { MdSend } from "react-icons/md";
import clsx from "clsx";
import { BiTrash } from "react-icons/bi";
import { FiPlay, FiPause } from "react-icons/fi";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toast } from "react-toastify";
import { compressFile } from "../../../../utils/compressFile";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../../../Context/ThemeContext";

const getFileExtension = (url) => {
  return url?.split(".").pop().toLowerCase();
};

function categorizeFile(file) {
  if (!file || !file.type) return "document";

  const type = file.type;
  if (!type) return "document";

  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";

  return "document";
}

const FileIcon = ({ url }) => {
  const ext = getFileExtension(url);

  const iconMap = {
    pdf: <FaFilePdf className="size-20 text-red-500" />,
    doc: <FaFileWord className="size-20 text-blue-600" />,
    docx: <FaFileWord className="size-20 text-blue-600" />,
    jpg: <FaFileImage className="size-20 text-green-500" />,
    png: <FaFileImage className="size-20 text-green-500" />,
    txt: <FaFileAlt className="size-20 text-gray-500" />,
    xls: <FaFileExcel className="size-20 text-green-600" />,
    xlsx: <FaFileExcel className="size-20 text-green-600" />,
    ppt: <FaFilePowerpoint className="size-20 text-orange-500" />,
    pptx: <FaFilePowerpoint className="size-20 text-orange-500" />,
    csv: <FaFileCsv className="size-20 text-blue-400" />,
    ttf: <FaFont className="size-20 text-purple-500" />,
    otf: <FaFont className="size-20 text-purple-500" />,
    ttc: <FaFont className="size-20 text-purple-500" />,
  };

  return iconMap[ext] || <FaFile className="size-20 text-gray-400" />;
};

const ffmpeg = new FFmpeg();

const GroupBottomTypeArea = ({
  onScheduleClick,
  sendMessage,
  sendFilesInMessage,
  showAddMenu,
  setShowAddMenu,
  groupActiveChat,
}) => {
  const { theme } = useTheme();
  const [groupMessage, setGroupMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // const [showAddMenu, setShowAddMenu] = useState(false);
  const [preventSubmit, setPreventSubmit] = useState(false);
  const [stream, setStream] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const textareaRef = useRef(null);
  const addMenuRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const datePickerRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const canvasRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);
  const [previewFiles, setPreviewFiles] = useState([]);

  // Close Emoji Picker when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };

    if (showAddMenu) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showAddMenu]);

  useEffect(() => {
    setGroupMessage("");
    setPreviewFiles([]);
    setIsRecording(false);
    setIsPaused(false);
    clearInterval(timerRef.current);
    timerRef.current = null;
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    sourceRef.current = null;
    setRecordingTime(0);
  }, [groupActiveChat]);

  // Close Date Picker when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showDatePicker]);

  const handleEmojiClick = (emojiObject) => {
    setGroupMessage((prev) => prev + emojiObject.emoji);
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      if (textarea.scrollHeight <= 160) {
        textarea.style.height = `${textarea.scrollHeight}px`;
        textarea.style.overflowY = "hidden";
      } else {
        textarea.style.height = `${160}px`;
        textarea.style.overflowY = "auto";
      }
    }
  }, [groupMessage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && e.shiftKey) {
        setPreventSubmit(true);
      } else if (e.key === "Enter" && !e.shiftKey && previewFiles.length > 0) {
        if (preventSubmit) {
          e.preventDefault();
          return;
        }
        e.preventDefault(); // only Enter: submit
        handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewFiles]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      setPreventSubmit(true);
    } else if (e.key === "Enter" && !e.shiftKey) {
      if (preventSubmit) {
        e.preventDefault();
        return;
      }
      e.preventDefault(); // only Enter: submit
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    setGroupMessage(e.target.value);
    setPreventSubmit(false); // reset prevent flag when message is edited
  };

  const handleFileUpload = async (event) => {
    if (event.target.files.length > 0) {
      if (previewFiles.length + event.target.files.length > 10) {
        toast.error("Maximum 10 files can be uploaded at a time");
        return;
      }
      // Check file sizes before compression
      const validFiles = Array.from(event.target.files).filter((file) => {
        if (file.size > 2 * 1024 * 1024 * 1024) {
          // 2GB in bytes
          toast.error(`The ${file.name} file is too large. Limit: 2 GB`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        return;
      }

      const compressedFiles = await compressFile(validFiles);
      setPreviewFiles((prevFiles) => [
        ...prevFiles,
        ...Array.from(compressedFiles),
      ]);
      setShowAddMenu(false);
    }
  };

  const handleSubmit = () => {
    if (isRecording) {
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });

        if (!ffmpeg.loaded) {
          await ffmpeg.load();
        }

        const inputFile = "voice_message.webm";
        const outputFile = "voice_message.m4a";

        // Manually convert Blob to Uint8Array
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        await ffmpeg.writeFile(inputFile, uint8Array);
        await ffmpeg.exec([
          "-i",
          inputFile,
          "-c:a",
          "aac",
          "-b:a",
          "128k",
          outputFile,
        ]);
        const data = await ffmpeg.readFile(outputFile);

        const m4aBlob = new Blob([data], { type: "audio/m4a" });
        const file = new File([m4aBlob], outputFile, {
          type: "audio/m4a",
          lastModified: Date.now(),
        });
        setShowAddMenu(false);
        sendFilesInMessage([file]);
        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
      };
      stopRecording();
      return;
    }
    const trimmed = groupMessage.trim();
    if (trimmed === "" && previewFiles.length === 0) return;
    if (trimmed !== "" && previewFiles.length === 0) {
      sendMessage(trimmed);
      setGroupMessage("");
    }
    if (previewFiles.length > 0) {
      sendFilesInMessage(previewFiles, trimmed);
      setPreviewFiles([]); // Clear preview after sending
    }
    setGroupMessage("");
    setPreventSubmit(false); // reset flag after submit
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setStream(stream);
    mediaRecorderRef.current = new MediaRecorder(stream);
    audioChunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (e) => {
      audioChunksRef.current.push(e.data);
    };

    mediaRecorderRef.current.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
    };

    mediaRecorderRef.current.start();

    // Analyser setup
    audioCtxRef.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    analyserRef.current = audioCtxRef.current.createAnalyser();
    sourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
    sourceRef.current.connect(analyserRef.current);

    analyserRef.current.fftSize = 64;
    const bufferLength = analyserRef.current.frequencyBinCount;
    dataArrayRef.current = new Uint8Array(bufferLength);

    // Start the timer
    timerRef.current = setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);

    setIsRecording(true);
  };

  const stopRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    if (
      mediaRecorderRef.current &&
      (mediaRecorderRef.current.state === "recording" ||
        mediaRecorderRef.current.state === "paused")
    ) {
      mediaRecorderRef.current.stop();
    }

    // Stop the timer
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRecordingTime(0);
  };

  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current);
    }
  };

  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  };

  // Add a function to remove a file from the preview
  const removePreviewFile = (index) => {
    setPreviewFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handlePaste = (event) => {
    const file = event.clipboardData.files[0];
    if (file) {
      setPreviewFiles([...previewFiles, file]);
    }
  };

  return (
    <div className="w-full px-4 relative">
      {previewFiles.length > 0 && (
        <>
          <div className="flex justify-end pb-2">
            <button
              className="py-1 bg-[var(--primary-color)] px-2 text-xs text-white rounded-full"
              onClick={() => setPreviewFiles([])}
            >
              Remove all
            </button>
          </div>
          <div className="flex justify-start gap-2 mb-3 w-full overflow-auto">
            {previewFiles.map((file, index) => (
              <div key={index} className="relative justify-self-center">
                {categorizeFile(file) == "image" ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="w-24 h-24 rounded-xl cursor-pointer"
                    onClick={() =>
                      window.open(URL.createObjectURL(file), "_blank")
                    }
                  />
                ) : (
                  <div className="items-center gap-2">
                    <div className="flex items-center gap-2">
                      {categorizeFile(file) == "document" && (
                        <FileIcon url={file.name} />
                      )}
                      {categorizeFile(file) == "audio" && (
                        <FaFileAudio className="size-20 text-red-500" />
                      )}
                      {categorizeFile(file) == "video" && (
                        <FaFileVideo className="size-20 text-purple-500" />
                      )}
                    </div>
                    <span className="text-sm">
                      {(() => {
                        const nameParts = file.name.split(".");
                        const extension = nameParts.pop(); // get file extension
                        const baseName = nameParts.join("."); // in case filename contains multiple dots
                        const shortName =
                          baseName.length > 10
                            ? baseName.slice(0, 5)
                            : baseName;
                        return `${shortName}.${extension}`;
                      })()}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => removePreviewFile(index)}
                  className="absolute top-0 right-0"
                >
                  <IoIosCloseCircle className="w-6 h-6" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      <form className="flex w-full items-center gap-3 relative z-10">
        {/* Add Icon (File Upload) */}
        {!isRecording ? (
          <>
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              className="hidden"
              multiple
              maxLength={2147483648}
              value=""
              accept=".doc,.docx,.pdf,.txt,.xls,.xlsx,.ppt,.pptx,.csv,.ttf,.otf,.ttc,.mp3,.wav,.m4a,.mp4,.mov,.avi,.wmv"
              onChange={handleFileUpload}
            />
            <input
              id="image-upload"
              name="image-upload"
              type="file"
              className="hidden"
              accept=".jpg,.png"
              multiple
              value=""
              onChange={handleFileUpload}
            />
            <div className="relative flex items-center">
              {showAddMenu ? (
                <label
                  className="p-2 text-gray-500 pl-0 hover:text-gray-900 cursor-pointer"
                  onClick={() => setShowAddMenu(false)}
                >
                  <IoMdClose className="w-6 h-6" />
                </label>
              ) : (
                <label
                  className="p-2 text-gray-500 pl-0 hover:text-gray-900 cursor-pointer"
                  onClick={() => setShowAddMenu(true)}
                >
                  <IoMdAdd className="w-6 h-6" />
                </label>
              )}

              {showAddMenu && (
                <div
                  ref={addMenuRef}
                  className="absolute bottom-15.5 left-[-32px] right-0 z-[100] bg-white dark:bg-slate-900 rounded-lg border-b rounded-l-none rounded-br-none p-2 py-6  min-w-[400px]"
                  style={{ pointerEvents: 'auto' }}
                >
                  <ul className="grid grid-cols-3 gap-6">
                    {/* <div className="flex items-center flex-col">
                      <li className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 cursor-pointer border border-[#D7D7D733] bg-[#FCFBFF] hover:bg-gray-100 p-3 rounded-xl">
                        <SlCamera className="text-[var(--primary-color)] w-5 h-5" />
                      </li>
                      <span className="text-xs text-black pt-2 font-medium">
                        Camera
                      </span>
                    </div> */}

                    <li className="flex flex-col w-full">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddMenu(false);
                          startRecording();
                        }}
                        className="flex flex-col items-center cursor-pointer w-full h-full border-0 bg-transparent p-0 relative"
                      >
                        <div className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 border border-[#D7D7D733] dark:border-slate-700 bg-[#FCFBFF] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 p-3 rounded-xl w-full pointer-events-none">
                          <HiOutlineMicrophone className="text-[#E67240] w-5 h-5" />
                        </div>
                        <span className="text-xs text-black dark:text-gray-200 pt-2 font-medium pointer-events-none">
                          Record
                        </span>
                      </button>
                    </li>

                    {/* <div className="flex items-center flex-col">
                      <li className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 cursor-pointer border border-[#D7D7D733] bg-[#FCFBFF] hover:bg-gray-100 p-3 rounded-xl">
                        <HiOutlineUser className="text-[#06B300] w-5 h-5" />
                      </li>
                      <span className="text-xs text-black pt-2 font-medium">
                        Contact
                      </span>
                    </div> */}

                    <li className="flex flex-col w-full">
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center cursor-pointer w-full h-full relative"
                      >
                        <div className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 border border-[#D7D7D733] dark:border-slate-700 bg-[#FCFBFF] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 p-3 rounded-xl w-full pointer-events-none">
                          <TfiGallery className="text-[#E63939] w-5 h-5" />
                        </div>
                        <span className="text-xs text-black dark:text-gray-200 pt-2 font-medium pointer-events-none">
                          Gallery
                        </span>
                      </label>
                    </li>

                    {/* <div className="flex items-center flex-col">
                      <li className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 cursor-pointer border border-[#D7D7D733] bg-[#FCFBFF] hover:bg-gray-100 p-3 rounded-xl">
                        <SlLocationPin className="text-[#FFB300] w-5 h-5" />
                      </li>
                      <span className="text-xs text-black pt-2 font-medium">
                        My Locations
                      </span>
                    </div> */}

                    <li className="flex flex-col w-full">
                      <label
                        htmlFor="file-upload"
                        className="flex flex-col items-center cursor-pointer w-full h-full relative"
                      >
                        <div className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 border border-[#D7D7D733] dark:border-slate-700 bg-[#FCFBFF] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 p-3 rounded-xl w-full pointer-events-none">
                          <IoDocumentTextOutline className="text-[#038FCE] w-5 h-5" />
                        </div>
                        <span className="text-xs text-black dark:text-gray-200 pt-2 font-medium pointer-events-none">
                          Documents
                        </span>
                      </label>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* <input
            type="file"
            className="hidden"
            accept="image/*,video/*"
            onChange={handleFileUpload}
          /> */}

            {/* Input Container */}
            <div className="flex w-full items-center rounded-lg border border-[#7C7C7C] dark:border-slate-600 py-1 p-2 relative">
              {/* Emoji Icon */}
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-gray-900"
                onClick={() => setShowEmojiPicker((prev) => !prev)}
              >
                <FaRegSmile className="w-6 h-6" />
              </button>

              {/* Emoji Picker */}
              <div
                ref={emojiPickerRef}
                className={`${showEmojiPicker ? "block" : "hidden"
                  } absolute bottom-14 left-0 z-10 bg-white shadow-lg rounded-md`}
              >
                <EmojiPicker theme={theme} onEmojiClick={handleEmojiClick} />
              </div>

              {/* Textarea */}
              <textarea
                id="chat"
                ref={textareaRef}
                rows="1"
                className="block mx-1 p-2 w-full text-sm text-gray-900 bg-transparent outline-none resize-none overflow-hidden dark:text-white"
                placeholder="Type a message..."
                value={groupMessage}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
              />
            </div>

            {/* Calendar Icon */}
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-gray-900 relative"
              onClick={onScheduleClick}
            >
              <LuCalendarPlus2 className="w-6 h-6" />
            </button>
            {previewFiles.length !== 0 && (
              <button
                type="button"
                onClick={handleSubmit}
                className="text-[#7104F8]"
              >
                <IoSendSharp className="size-7" />
              </button>
            )}
          </>
        ) : (
          <>
            <div className="flex-1 gap-5 flex justify-between items-center">
              <button
                type="button"
                onClick={stopRecording}
                className="text-red-600"
              >
                <BiTrash className="size-7" />
              </button>
              <div className="flex bg-[#8d36f9] p-2 rounded-md">
                <button
                  type="button"
                  onClick={isPaused ? resumeRecording : pauseRecording}
                  className="text-white flex items-center justify-center mr-5"
                >
                  {isPaused ? (
                    <FiPlay className="size-5" />
                  ) : (
                    <FiPause className="size-7" />
                  )}
                </button>
                <div className="flex gap-[5px] items-center justify-center overflow-hidden">
                  {[
                    4, 8, 12, 16, 20, 24, 28, 30, 28, 26, 24, 22, 20, 18, 16,
                    14, 12, 10, 8, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26,
                  ].map((h, i) => (
                    <div
                      key={i}
                      className={clsx(
                        "w-[3px] bg-white rounded-full",
                        !isPaused && "animate-wave"
                      )}
                      style={{
                        height: `${h}px`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>
                <div className="text-white mt-1 ml-5">
                  {Math.floor(recordingTime / 60)}:
                  {recordingTime % 60 < 10 ? "0" : ""}
                  {recordingTime % 60}
                </div>
              </div>
              <div></div>
            </div>
          </>
        )}
        {/* Microphone Icon */}
        {previewFiles.length <= 0 && (
          <>
            {groupMessage.trim() || isRecording ? (
              <button
                onClick={handleSubmit}
                type="button"
                className="p-3 text-white bg-[var(--primary-color)] rounded-full hover:bg-blue-600"
              >
                <MdSend className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={startRecording}
                className="p-3 text-white bg-[var(--primary-color)] rounded-full hover:bg-blue-600"
              >
                <FaMicrophone className="w-5 h-5" />
              </button>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default GroupBottomTypeArea;
